import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { Plus, Loader2, Trash2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/Logo";
import AiAssistant from "@/components/recipes/AiAssistant";
import LineRow from "@/components/recipes/LineRow";
import CostBreakdown from "@/components/recipes/CostBreakdown";
import { useAuth } from "@/context/AuthContext";
import api, { errMsg } from "@/lib/api";
import { convertQty, RECIPE_CATEGORIES, catLabel } from "@/lib/format";
import { t } from "@/i18n";

const toNum = (v) => parseFloat(String(v).replace(",", ".")) || 0;
let keySeq = 0;
const withKey = (l) => ({ ...l, key: ++keySeq });

export default function RecipeEditor() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const target = user?.restaurant?.target_food_cost || 30;
  const [ingredients, setIngredients] = useState([]);
  const [form, setForm] = useState({ name: "", category: "Plats", selling_price: "", description: "" });
  const [lines, setLines] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(!id);
  const [showAi, setShowAi] = useState(!id || params.get("ai") === "1");

  useEffect(() => {
    api.get("/ingredients").then(({ data }) => setIngredients(data));
    if (id) {
      api.get(`/recipes/${id}`).then(({ data }) => {
        setForm({ name: data.name, category: data.category, selling_price: data.selling_price, description: data.description || "" });
        setLines(data.ingredients.map((l) => withKey({ ingredient_id: l.ingredient_id, quantity: l.quantity, unit: l.unit, new_ingredient: null })));
        setShowAi(false);
        setLoaded(true);
      }).catch(() => nav("/recipes"));
    }
  }, [id, nav]);

  const costed = useMemo(() => lines.map((l) => {
    const ing = ingredients.find((i) => i.id === l.ingredient_id);
    if (ing) return { name: ing.name, cost: convertQty(toNum(l.quantity), l.unit, ing.unit) * ing.current_unit_cost };
    if (l.new_ingredient) return { name: l.new_ingredient.name || t("common.nouvel_ingredient"), cost: convertQty(toNum(l.quantity), l.unit, l.new_ingredient.unit) * toNum(l.new_ingredient.unit_cost) };
    return { name: "", cost: 0 };
  }), [lines, ingredients]);

  const price = toNum(form.selling_price);
  const cost = costed.reduce((s, l) => s + l.cost, 0);
  const m = { cost, margin: price - cost, foodCost: price ? (cost / price) * 100 : 0, marginPct: price ? ((price - cost) / price) * 100 : 0 };

  const applyAi = (r) => {
    setForm({ name: r.name, category: RECIPE_CATEGORIES.includes(r.category) ? r.category : "Plats", selling_price: r.suggested_price || "", description: r.description || "" });
    setLines(r.ingredients.map((it) => withKey(it.ingredient_id
      ? { ingredient_id: it.ingredient_id, quantity: it.quantity, unit: it.unit, new_ingredient: null }
      : { ingredient_id: null, quantity: it.quantity, unit: it.unit, new_ingredient: { name: it.name, unit: it.ingredient_unit, unit_cost: it.estimated_unit_cost, category: it.category } })));
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error(t("recipeEditor.donnez_un_nom_a_la_recette"));
    if (lines.some((l) => !l.ingredient_id && !l.new_ingredient?.name?.trim())) return toast.error(t("recipeEditor.chaque_ligne_doit_avoir_un_ingredient"));
    setBusy(true);
    const body = {
      ...form, selling_price: price,
      ingredients: lines.filter((l) => toNum(l.quantity) > 0).map((l) => ({
        ingredient_id: l.ingredient_id, quantity: toNum(l.quantity), unit: l.unit,
        new_ingredient: l.new_ingredient ? { ...l.new_ingredient, unit_cost: toNum(l.new_ingredient.unit_cost) } : null,
      })),
    };
    try {
      if (id) await api.put(`/recipes/${id}`, body);
      else await api.post("/recipes", body);
      toast.success(t("recipeEditor.recette_enregistree"));
      nav("/recipes");
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(t("recipeEditor.supprimer_cette_recette"))) return;
    await api.delete(`/recipes/${id}`);
    toast.success(t("recipeEditor.recette_supprimee"));
    nav("/recipes");
  };

  if (!loaded) return <div className="flex h-64 items-center justify-center" data-testid="recipe-loading"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <>
      <Button variant="ghost" size="sm" asChild className="-ml-2 mb-2" data-testid="back-to-recipes-btn"><Link to="/recipes"><ArrowLeft className="mr-1 h-4 w-4" />{t("common.recettes")}</Link></Button>
      <PageHeader eyebrow={id ? t("recipeEditor.modifier") : t("recipeEditor.nouvelle_fiche_technique")} title={form.name || (id ? t("common.recette") : t("recipeEditor.nouvelle_recette"))}
        actions={<>
          {!showAi && <Button variant="outline" onClick={() => setShowAi(true)} data-testid="show-ai-assistant-btn">{t("common.assistant_ia")}</Button>}
          {id && <Button variant="outline" onClick={remove} data-testid="delete-recipe-btn"><Trash2 className="mr-2 h-4 w-4" />{t("common.supprimer")}</Button>}
          <Button onClick={save} disabled={busy} data-testid="save-recipe-btn">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{t("common.enregistrer")}</Button>
        </>} />
      {showAi && <AiAssistant onResult={applyAi} />}
      <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
        <div className="space-y-6">
          <div className="grid gap-4 rounded-2xl border bg-card p-5 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label>{t("recipeEditor.nom_du_plat")}</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("recipeEditor.burger_deluxe")} data-testid="recipe-name-input" />
            </div>
            <div className="space-y-2">
              <Label>{t("common.categorie")}</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="recipe-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{RECIPE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{catLabel(c)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("recipeEditor.prix_de_vente_ht")}</Label>
              <Input className="num" inputMode="decimal" value={form.selling_price} onChange={(e) => setForm({ ...form, selling_price: e.target.value })} placeholder="14,50" data-testid="recipe-price-input" />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>{t("recipeEditor.description")}</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="recipe-description-input" />
            </div>
          </div>
          <div className="rounded-2xl border bg-card p-5">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h3 className="font-semibold">{t("common.ingredients")}</h3>
                <p className="text-xs text-muted-foreground">{t("recipeEditor.quantites_pour_une_portion")}</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => setLines([...lines, withKey({ ingredient_id: ingredients[0]?.id || null, quantity: 100, unit: ingredients[0] ? (ingredients[0].unit === "pièce" ? "pièce" : ingredients[0].unit === "L" ? "ml" : "g") : "g", new_ingredient: ingredients[0] ? null : { name: "", unit: "kg", unit_cost: 0, category: "Autre" } })])} data-testid="add-recipe-line-btn">
                <Plus className="mr-1.5 h-3.5 w-3.5" />{t("common.ajouter")}
              </Button>
            </div>
            <div className="space-y-2">
              {lines.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">{t("recipeEditor.aucun_ingredient_ajoutez_en_ou_utilisez_l")}</p>}
              {lines.map((l, i) => (
                <LineRow key={l.key} line={l} idx={i} ingredients={ingredients} cost={costed[i]?.cost || 0}
                  onChange={(nl) => setLines(lines.map((x) => (x.key === l.key ? nl : x)))}
                  onRemove={() => setLines(lines.filter((x) => x.key !== l.key))} />
              ))}
            </div>
          </div>
        </div>
        <CostBreakdown m={m} price={price} target={target} lines={costed} />
      </div>
    </>
  );
}
